import { XCircleIcon } from "@heroicons/react/20/solid";
import clsx from "clsx";
import { type FunctionComponent, memo } from "react";

import type { ButtonVariantsProps } from "~/components/buttons/Button/buttonVariants.ts";
import type { FormInputBaseVariants } from "~/components/formElements/formInputBaseVariant.ts";
import { formInputBaseVariants } from "~/components/formElements/formInputBaseVariant.ts";
import useOnClickOutside from "~/hooks/useOnClickOutside.ts";
import DeliveryPeriodPickerPopover
	from "~/modules/billing/components/DeliveryPeriodFilter/components/DeliveryPeriodPickerPopover.tsx";
import type { TempDateRange } from "~/modules/billing/components/DeliveryPeriodFilter/useDeliveryPeriodPicker.ts";
import {
	computeDateRange,
	useDeliveryPeriodPicker,
} from "~/modules/billing/components/DeliveryPeriodFilter/useDeliveryPeriodPicker.ts";
import type { DateRangeNullable } from "~/types/dateAndTimeTypes.ts";


type Props = ButtonVariantsProps & FormInputBaseVariants & {
	selectedPeriod: DateRangeNullable;
	onChange: (period: DateRangeNullable) => void;
};

const DeliveryPeriodFilter: FunctionComponent<Props> = memo(({
	selectedPeriod,
	onChange,
	inputSize,
}) => {
	const startDate = selectedPeriod.startDate;
	const endDate = selectedPeriod.endDate;

	const {
		isOpen,
		setIsOpen,
		refs,
		floatingStyles,
		context,
		getReferenceProps,
		getFloatingProps,
		tempDateRange,
		setTempDateRange,
		quickSelectOptions,
		activeQuickSelect,
		buttonText,
		now,
	} = useDeliveryPeriodPicker({ startDate, endDate });

	const handleRangeChange = (value: TempDateRange) => {
		const [tempStart, tempEnd] = value;
		setTempDateRange(value);
		const computed = computeDateRange(tempStart, tempEnd);
		onChange(computed);
	};

	const applyFilter = () => {
		const [tempStart, tempEnd] = tempDateRange;
		const computed = computeDateRange(tempStart, tempEnd);
		onChange(computed);
		setIsOpen(false);
	};

	const handlePopoverClose = () => {
		applyFilter();
	};

	const handleClear = () => {
		onChange({ startDate: null, endDate: null });
		setTempDateRange([null, null]);
	};

	useOnClickOutside(refs.floating, handlePopoverClose);

	return (
		<>
			<div className="relative">
				<button
					className={clsx(formInputBaseVariants({ inputSize }))}
					ref={refs.setReference}
					{...getReferenceProps({ onClick: (e) => e.stopPropagation() })}
				>
					<span className={(!startDate && !endDate) ? "text-gray-400" : ""}>
						{buttonText}
					</span>
				</button>
				{startDate && endDate && !isOpen && (
					<div className="absolute right-0 top-0 h-full">
						<button
							onClick={handleClear}
							className="flex h-full items-center pr-2"
						>
							<XCircleIcon className="size-5 fill-gray-300" />
						</button>
					</div>
				)}
			</div>

			<DeliveryPeriodPickerPopover
				isOpen={isOpen}
				context={context}
				refs={refs}
				floatingStyles={floatingStyles}
				getFloatingProps={getFloatingProps}
				tempDateRange={tempDateRange}
				onRangeChange={handleRangeChange}
				quickSelectOptions={quickSelectOptions}
				activeQuickSelect={activeQuickSelect}
				now={now}
			/>
		</>
	);
});

DeliveryPeriodFilter.displayName = "DeliveryPeriodFilter";

export default DeliveryPeriodFilter;